/**
 * Top-level error boundary.
 *
 * The legacy app had none: a render error blanked the page with no explanation and no
 * way back. This shows what happened and offers a recovery path.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { errorMessage } from '@/lib/api/errors';
import { isChunkLoadError } from './routes/lazyRoute';
import styles from './ErrorBoundary.module.css';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A chunk this build no longer has. Resetting state cannot help: React
    // caches the rejected lazy() promise, so the retry throws the same error
    // straight back. Only a fresh index.html names the new file.
    const stale = isChunkLoadError(error);

    return (
      <div className={styles.container} role="alert">
        <div className={styles.card}>
          <h1 className={styles.title}>
            {stale ? 'A new version is available' : 'Something went wrong'}
          </h1>
          <p className={styles.message}>
            {stale
              ? 'This page was updated while the app was open. Reload to continue.'
              : errorMessage(error)}
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              onClick={stale ? () => window.location.reload() : this.reset}
            >
              {stale ? 'Reload' : 'Try again'}
            </button>
            <a className={styles.secondary} href="/">
              Go to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
